import { useEffect, useState, useMemo } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ChevronLeft, ChevronRight as ChevronRightIcon, ChevronDown } from 'lucide-react'
import { contentApi, type ContentCategory, type ContentAudience, type ContentListItem } from '../../lib/api'
import { getCategoryVisual } from '../../lib/categoryIcons'
import ContentCard from '../../components/ContentCard'
import EmptyState from '../../components/EmptyState'
import { usePageTitle } from '../../hooks/usePageTitle'

const PAGE_SIZE = 10

/** 列表项骨架屏 */
const CardSkeleton = () => (
  <div className="bg-white rounded-2xl border border-gray-100 p-3 flex gap-3 animate-pulse">
    <div className="w-28 h-20 bg-gray-100 rounded-xl flex-shrink-0" />
    <div className="flex-1 space-y-2 py-0.5">
      <div className="h-3.5 bg-gray-100 rounded w-full" />
      <div className="h-3.5 bg-gray-100 rounded w-2/3" />
      <div className="h-3 bg-gray-100 rounded w-1/3 mt-1.5" />
    </div>
  </div>
)

/** 筛选分组横向滚动条 */
const FilterRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex items-center gap-2">
    <span className="text-[11px] text-gray-400 flex-shrink-0">{label}</span>
    <div className="flex gap-1.5 overflow-x-auto flex-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {children}
    </div>
  </div>
)

const chip = (active: boolean) =>
  `text-xs px-3 py-1.5 rounded-full border flex-shrink-0 transition-colors ${
    active
      ? 'bg-green-600 text-white border-green-600 font-medium'
      : 'bg-white text-gray-600 border-gray-200 hover:border-green-300 hover:text-green-700'
  }`

const CategoryList = () => {
  usePageTitle()
  const navigate = useNavigate()
  const { categoryId } = useParams<{ categoryId: string }>()
  const [params, setParams] = useSearchParams()
  const [tree, setTree] = useState<ContentCategory[]>([])
  const [audiences, setAudiences] = useState<ContentAudience[]>([])
  const [items, setItems] = useState<ContentListItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const audienceCode = params.get('audience_code') || ''
  const mediaType = params.get('media_type') || ''
  const childId = params.get('child_id') || ''
  const page = Number(params.get('page') || 1)

  const current = useMemo(
    () => tree.find(c => c.id === categoryId),
    [tree, categoryId]
  )
  const children = useMemo(
    // 树接口返回嵌套结构，子分类挂在当前节点的 children 上（平铺过滤会得到空数组）
    () => current?.children || [],
    [current]
  )

  useEffect(() => {
    contentApi.getCategoryTree().then(setTree).catch(() => setTree([]))
    contentApi.getAudiences().then(setAudiences).catch(() => setAudiences([]))
  }, [])

  useEffect(() => {
    window.scrollTo(0, 0)
    setLoading(true); setError('')
    contentApi.listPublic({
      category_id: childId || categoryId,
      audience_code: audienceCode || undefined,
      media_type: mediaType || undefined,
      page,
      page_size: PAGE_SIZE,
    }).then(res => {
      setItems(res.items)
      setTotal(res.total)
    }).catch(e => setError(e.message || '加载失败'))
      .finally(() => setLoading(false))
  }, [categoryId, childId, audienceCode, mediaType, page])

  const updateParam = (k: string, v: string) => {
    const np = new URLSearchParams(params)
    if (v) np.set(k, v); else np.delete(k)
    if (k !== 'page') np.delete('page')
    setParams(np)
  }
  const clearFilters = () => setParams(new URLSearchParams())

  const activeCount = [audienceCode, mediaType, childId].filter(Boolean).length
  const totalPages = Math.ceil(total / PAGE_SIZE)
  const visual = getCategoryVisual(current?.name || '', 0)

  return (
    <div className="min-h-screen bg-gray-50 pb-6">
      {/* 顶部分类头 */}
      <div className="bg-gradient-to-br from-green-700 via-green-600 to-emerald-500">
        <div className="relative z-10 px-4 pt-4 pb-8 max-w-3xl mx-auto">
          <button
            onClick={() => navigate('/user/knowledge')}
            className="inline-flex items-center gap-1 text-xs text-green-50/90 hover:text-white"
          >
            <ChevronLeft className="w-4 h-4" />科普中心
          </button>
          <div className="flex items-center gap-3 mt-3">
            <div className={`w-11 h-11 rounded-2xl ${visual.bg} ${visual.fg} flex items-center justify-center flex-shrink-0 shadow-sm`}>
              <visual.Icon className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">{current?.name || '加载中…'}</h1>
              <p className="text-green-100 text-[11px] mt-0.5">共 <b className="text-white">{total}</b> 条内容</p>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-5">
          <svg viewBox="0 0 1440 40" preserveAspectRatio="none" className="w-full h-full">
            <path d="M0,40 C360,0 1080,0 1440,40 L1440,40 L0,40 Z" fill="#f9fafb" />
          </svg>
        </div>
      </div>

      <div className="px-4 -mt-4 relative z-10 max-w-3xl mx-auto space-y-3">
        {/* 二级分类：常驻横向 tab（有子分类时展示），点选即筛选 */}
        {children.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-3 py-2.5">
            <div className="flex gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <button onClick={() => updateParam('child_id', '')} className={chip(!childId)}>全部</button>
              {children.map(c => {
                const v = getCategoryVisual(c.name, 0)
                return (
                  <button
                    key={c.id}
                    onClick={() => updateParam('child_id', childId === c.id ? '' : c.id)}
                    className={`text-xs px-3 py-1.5 rounded-full border flex-shrink-0 flex items-center gap-1.5 transition-colors ${
                      childId === c.id
                        ? 'bg-green-600 text-white border-green-600 font-medium'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-green-300 hover:text-green-700'
                    }`}
                  >
                    <span className={`w-4 h-4 rounded-md ${v.bg} ${v.fg} flex items-center justify-center`}>
                      <v.Icon className="w-2.5 h-2.5" />
                    </span>
                    {c.name}
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {/* 筛选面板（可折叠） */}
        <details className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" open={!!activeCount}>
          <summary className="flex items-center justify-between px-4 py-3 cursor-pointer select-none list-none [&::-webkit-details-marker]:hidden">
            <span className="text-[13px] font-medium text-gray-700 flex items-center gap-1.5">
              筛选
              {activeCount > 0 && (
                <span className="w-4 h-4 rounded-full bg-green-600 text-white text-[10px] flex items-center justify-center">{activeCount}</span>
              )}
            </span>
            <span className="flex items-center gap-3">
              {activeCount > 0 && (
                <button
                  onClick={e => { e.preventDefault(); clearFilters() }}
                  className="text-xs text-green-600 hover:text-green-700"
                >清空</button>
              )}
              <ChevronDown className="w-4 h-4 text-gray-400" />
            </span>
          </summary>
          <div className="px-4 pb-3.5 space-y-2.5 pt-1 border-t border-gray-50">
            {audiences.length > 0 && (
              <FilterRow label="适用人群">
                <button onClick={() => updateParam('audience_code', '')} className={chip(!audienceCode)}>全部</button>
                {audiences.map(a => (
                  <button key={a.code} onClick={() => updateParam('audience_code', a.code)} className={chip(audienceCode === a.code)}>{a.name}</button>
                ))}
              </FilterRow>
            )}
            <FilterRow label="内容类型">
              <button onClick={() => updateParam('media_type', '')} className={chip(!mediaType)}>全部</button>
              {([['text', '图文'], ['video', '视频'], ['audio', '音频']] as const).map(([mt, label]) => (
                <button key={mt} onClick={() => updateParam('media_type', mt)} className={chip(mediaType === mt)}>{label}</button>
              ))}
            </FilterRow>
          </div>
        </details>

        {/* 内容列表 */}
        {error ? (
          <EmptyState variant="error" description={error} action={{ label: '重试', onClick: () => updateParam('page', String(page)) }} />
        ) : loading ? (
          <div className="space-y-2.5">
            {Array.from({ length: 5 }).map((_, i) => <CardSkeleton key={i} />)}
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            title="暂无内容"
            description="该分类下暂无内容，或尝试调整筛选条件"
            action={activeCount > 0 ? { label: '清除筛选', onClick: clearFilters } : undefined}
          />
        ) : (
          <>
            <div className="space-y-2.5">
              {items.map(item => (
                <ContentCard key={item.id} item={item} variant="compact" />
              ))}
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-1.5 pt-3">
                <button
                  disabled={page <= 1}
                  onClick={() => updateParam('page', String(page - 1))}
                  className="p-2 rounded-full bg-white border border-gray-200 disabled:opacity-40 hover:border-green-300 hover:text-green-700 transition-colors"
                ><ChevronLeft className="w-4 h-4" /></button>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  const start = Math.max(1, Math.min(page - 2, totalPages - 4))
                  return start + i
                }).filter(p => p >= 1 && p <= totalPages).map(p => (
                  <button
                    key={p}
                    onClick={() => updateParam('page', String(p))}
                    className={`w-7 h-7 rounded-full text-[13px] font-medium transition-colors ${
                      p === page
                        ? 'bg-green-600 text-white shadow-sm shadow-green-200'
                        : 'bg-white border border-gray-200 text-gray-600 hover:border-green-300 hover:text-green-700'
                    }`}
                  >{p}</button>
                ))}
                <button
                  disabled={page >= totalPages}
                  onClick={() => updateParam('page', String(page + 1))}
                  className="p-2 rounded-full bg-white border border-gray-200 disabled:opacity-40 hover:border-green-300 hover:text-green-700 transition-colors"
                ><ChevronRightIcon className="w-4 h-4" /></button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default CategoryList
